import React, { FC, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

import TippyTooltip from '@components/common/tooltip/TippyTooltip';
import MaterialIcon from '@components/common/icons/MaterialIcon';
import t from '@i18n/t';
import colors from '@utils/colors';
import { PERSONALIZATION_SETTINGS } from '@utils/constants/routes';
import {
  formatMinuteOfDay,
  isDarkHour,
  minuteOfDay,
  resolveTheme,
  SCROLL_TO_APPEARANCE,
  systemPrefersDark,
  ThemePreference
} from '@utils/theme/themePreference';
import { useThemeMode } from '@utils/theme/themeMode';

/**
 * Light or dark, in the sidebar. A click flips between the two at once; pointing at it offers Light, Dark, Auto, which
 * follows the device, and set hours, dark between two times of day (Petr, 2026-10-08). It replaced a Lottie that only had the sun-to-moon half, so going back
 * to light never animated, and that switched the page only once it had finished playing.
 */

// The moon keeps its own purple whatever colour scheme is chosen, close to the old animation's.
const SUN_COLOR = colors.warning;
const MOON_COLOR = '#8E83E8';

const RAY_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];
const rayEnd = (angle: number, radius: number) => {
  const rad = (angle * Math.PI) / 180;
  return { x: 12 + radius * Math.sin(rad), y: 12 - radius * Math.cos(rad) };
};

const OPTIONS: { preference: ThemePreference; label: string; icon: string; hint?: string }[] = [
  { preference: 'light', label: t.LIGHT, icon: 'light_mode' },
  { preference: 'dark', label: t.DARK, icon: 'dark_mode' },
  { preference: 'auto', label: t.AUTO, icon: 'brightness_auto', hint: t.MATCH_YOUR_DEVICE },
  // Its hint is the hours themselves, filled in as the popup draws.
  { preference: 'scheduled', label: t.SET_HOURS, icon: 'schedule' }
];

// Switching redraws the whole app, which holds the page still for a moment (about a second on a dev server), and an
// icon animating through that froze and only moved once the page had changed. So the icon plays first and the page
// follows once the move has mostly settled, as the old Lottie did.
const SWITCH_AFTER_MS = 450;

let maskCount = 0;

const ThemeToggle: FC = () => {
  const { preference, theme, darkHours, setPreference } = useThemeMode();
  const [maskId] = useState(() => `theme-toggle-bite-${++maskCount}`);
  // What the icon shows while it plays ahead of the page.
  const [pending, setPending] = useState<ThemePreference | null>(null);
  const timer = useRef<number>();
  // The popup, so following its link to Settings can close it rather than leave it over the page it opens.
  const popup = useRef<{ hide: () => void } | null>(null);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const shown = pending ?? preference;
  const dark =
    (pending ? resolveTheme(pending, systemPrefersDark(), isDarkHour(minuteOfDay(), darkHours)) : theme) === 'dark';
  // The A stands for either way of switching by itself.
  const auto = shown === 'auto' || shown === 'scheduled';
  const hoursHint = `${t.DARK} ${formatMinuteOfDay(darkHours.from)} – ${formatMinuteOfDay(darkHours.to)}`;

  // A second change while one is playing replaces it; one back to what the page shows just plays the icon back.
  const change = (next: ThemePreference) => {
    window.clearTimeout(timer.current);
    if (next === preference) {
      setPending(null);
      return;
    }
    setPending(next);
    timer.current = window.setTimeout(() => {
      setPreference(next);
      setPending(null);
    }, SWITCH_AFTER_MS);
  };

  // The click flips what shows, which also leaves Auto or set hours.
  const toggle = () => change(dark ? 'light' : 'dark');

  const menu = (
    <Options>
      {OPTIONS.map(option => {
        const selected = option.preference === shown;
        const hint = option.preference === 'scheduled' ? hoursHint : option.hint;
        return (
          <React.Fragment key={option.preference}>
            <Option type="button" $selected={selected} aria-pressed={selected} onClick={() => change(option.preference)}>
              <MaterialIcon name={option.icon} size={16} outline color="currentColor" />
              <OptionText>
                {option.label}
                {hint && <OptionHint>{hint}</OptionHint>}
              </OptionText>
              <Check $visible={selected}>
                <MaterialIcon name="check" size={16} color="currentColor" />
              </Check>
            </Option>
            {option.preference === 'scheduled' && (
              <ChangeHours
                to={{ pathname: PERSONALIZATION_SETTINGS, state: { scrollTo: SCROLL_TO_APPEARANCE } }}
                onClick={() => popup.current?.hide()}
              >
                {t.CHANGE_HOURS}
              </ChangeHours>
            )}
          </React.Fragment>
        );
      })}
    </Options>
  );

  return (
    <TippyTooltip
      target={
        <Toggle
          type="button"
          onClick={toggle}
          aria-label={`${dark ? t.SWITCH_TO_LIGHT_MODE : t.SWITCH_TO_DARK_MODE}${
            shown === 'auto' ? ` (${t.AUTO_THEME_ON})` : shown === 'scheduled' ? ` (${t.SET_HOURS_ON}: ${hoursHint})` : ''
          }`}
        >
          <Icon width={22} height={22} viewBox="0 0 24 24" $dark={dark} $auto={auto} aria-hidden>
            <defs>
              <mask id={maskId} maskUnits="userSpaceOnUse" x={-6} y={-6} width={36} height={36}>
                <rect x={-6} y={-6} width={36} height={36} fill="#fff" />
                <circle className="bite" cx={17.5} cy={6.5} r={6.5} fill="#000" />
              </mask>
            </defs>
            <circle className="core" cx={12} cy={12} r={8.5} fill="currentColor" mask={`url(#${maskId})`} />
            <g className="rays" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              {RAY_ANGLES.map(angle => {
                const from = rayEnd(angle, 7.2);
                const to = rayEnd(angle, 9.6);
                return <line key={angle} x1={from.x} y1={from.y} x2={to.x} y2={to.y} />;
              })}
            </g>
            <g className="badge">
              <circle cx={20} cy={20} r={4.6} />
              <text x={20} y={22.5} textAnchor="middle">
                A
              </text>
            </g>
          </Icon>
        </Toggle>
      }
      content={menu}
      onCreate={instance => {
        popup.current = instance;
      }}
      position={'right'}
      interactive
      // A click flips the theme; the choices stay open so the new one shows ticked.
      hideOnClick={false}
      lightTheme
      noPadding
      arrow={false}
      width={'none'}
    />
  );
};

export default ThemeToggle;

const Toggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  /* The rail's icons sit about 40px apart, centre to centre. */
  margin: 0 0 8px;
  padding: 0;
  border: none;
  border-radius: 5px;
  background: none;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.backgroundDark};
  }
`;

// Both ways are the same moves reversed: the sun's core grows into the moon as a shadow slides across it and the rays
// fold away, turning; the A for Auto pops in at the corner.
const Icon = styled.svg<{ $dark: boolean; $auto: boolean }>`
  overflow: visible;
  color: ${({ $dark }) => ($dark ? MOON_COLOR : SUN_COLOR)};
  transition: color 0.45s ease;

  .core,
  .bite,
  .badge {
    transform-box: fill-box;
    transform-origin: center;
  }

  .core,
  .bite,
  .rays,
  .badge {
    transition: transform 0.55s cubic-bezier(0.3, 1.5, 0.5, 1), opacity 0.35s ease;
  }

  .core {
    transform: scale(${({ $dark }) => ($dark ? 1 : 0.52)});
  }

  .bite {
    transform: ${({ $dark }) => ($dark ? 'none' : 'translate(9px, -9px)')};
  }

  .rays {
    transform-origin: 12px 12px;
    ${({ $dark }) =>
      $dark
        ? css`
            opacity: 0;
            transform: rotate(90deg) scale(0.4);
          `
        : css`
            opacity: 1;
            transform: none;
          `}
  }

  .badge {
    opacity: ${({ $auto }) => ($auto ? 1 : 0)};
    transform: scale(${({ $auto }) => ($auto ? 1 : 0.3)});

    circle {
      fill: ${({ theme }) => theme.primary};
    }

    text {
      fill: ${colors.white};
      font-family: 'Quicksand', sans-serif;
      font-size: 7px;
      font-weight: 700;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    .core,
    .bite,
    .rays,
    .badge {
      transition: none;
    }
  }
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px 0;
  min-width: 170px;
`;

const Option = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font-family: 'Quicksand', sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: ${({ $selected, theme }) => ($selected ? theme.primary : theme.textColor)};

  &:hover {
    color: ${({ $selected, theme }) => ($selected ? theme.primary : theme.textStrongColor)};
    background-color: ${({ theme }) => theme.backgroundLight};
  }
`;

const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const OptionHint = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.disabledTextColor};
`;

// Under the set hours' own row, lined up with its text: the hours are changed in Settings.
const ChangeHours = styled(Link)`
  align-self: flex-start;
  margin: -4px 0 4px 34px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Check = styled.span<{ $visible: boolean }>`
  display: flex;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`;
